import { useRef, useCallback } from 'react';

/**
 * Hook customizado para gerenciar operações de drag and drop para reordenação de listas.
 *
 * @template T
 * @param {T[]} items - Array de itens a serem reordenados
 * @param {(newItems: T[]) => void} setItems - Função setState para atualizar o array
 * @param {(items: T[]) => void} [onReorderComplete] - Callback opcional executado após reordenação
 * @param {Object} options
 * @param {'shift' | 'swap'} [options.reorderingMode = 'shift'] - Como deve ocorrer o reorder (shift -> desloca as colunas para a direita / swap -> troca as colunas de lugar)
 * @returns {Object} Handlers de eventos e refs para implementação de drag and drop
 */
export const useDragAndReorder = (
  items,
  setItems,
  onReorderComplete,
  options = { reorderingMode: 'shift' }
) => {
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const lastEnteredItemRef = useRef(null); // Ref para rastrear o último item destacado

  /**
   * Manipulador para o evento dragStart
   * @param {React.DragEvent} e - Evento de drag
   * @param {number} index - Índice do item sendo arrastado
   */
  const handleDragStart = useCallback((e, index) => {
    dragItem.current = index;
    // Adiciona estilo visual ao elemento sendo arrastado
    e.currentTarget.style.opacity = '0.6';

    // Define os dados a serem transferidos (opcional)
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
    }
  }, []);

  /**
   * Manipulador para o evento dragEnter
   * @param {React.DragEvent} e - Evento de drag
   * @param {number} index - Índice do item sob o cursor
   */
  const handleDragEnter = useCallback((e, index) => {
    dragOverItem.current = index;
    e.preventDefault();

    // Destaca elemento sobre o qual estamos arrastando
    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
    lastEnteredItemRef.current = e.currentTarget;
  }, []);

  /**
   * Manipulador para o evento dragLeave
   * @param {React.DragEvent} e - Evento de drag
   */
  const handleDragLeave = useCallback((e) => {
    // Remove destaque
    e.currentTarget.style.background = '';
  }, []);

  /**
   * Manipulador para o evento dragOver
   * @param {React.DragEvent} e - Evento de drag
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();

    // Manter cursor apropriado
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  }, []);

  /**
   * Manipulador para o evento dragEnd
   * @param {React.DragEvent} e - Evento de drag
   */
  const handleDragEnd = useCallback(
    (e) => {
      // Restaura estilo do elemento
      e.currentTarget.style.opacity = '';
      e.currentTarget.style.background = '';

      // Remove destaque do último item sobre o qual se passou
      if (lastEnteredItemRef.current) {
        lastEnteredItemRef.current.style.background = '';
        lastEnteredItemRef.current = null;
      }

      if (dragItem.current === null || dragOverItem.current === null) {
        return;
      }

      // Impede reordenação se tentando soltar no mesmo índice
      if (dragItem.current === dragOverItem.current) return;

      let newItems = [...items];
      const dragItemContent = newItems[dragItem.current];

      if (options.reorderingMode === 'shift') {
        // Remove o item da posição original e insere na nova posição
        newItems.splice(dragItem.current, 1);
        newItems.splice(dragOverItem.current, 0, dragItemContent);
      } else if (options.reorderingMode === 'swap') {
        // Troca os itens de posição
        [newItems[dragItem.current], newItems[dragOverItem.current]] = [
          newItems[dragOverItem.current],
          newItems[dragItem.current]
        ];
      }

      // Atualiza o estado com a nova ordem
      setItems(newItems);

      // Chama o callback de conclusão se fornecido
      if (onReorderComplete) {
        onReorderComplete(newItems);
      }

      // Limpa as referências
      dragItem.current = null;
      dragOverItem.current = null;
    },
    [items, setItems, onReorderComplete, options.reorderingMode]
  );

  return {
    dragItem,
    dragOverItem,
    handleDragStart,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDragEnd
  };
};
